import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { connectWallet, getCurrentAccount, getProvider, switchToPolygonAmoy } from '../services/web3';

interface UseWeb3Return {
  account: string | null;
  provider: ethers.BrowserProvider | null;
  signer: ethers.JsonRpcSigner | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

export const useWeb3 = (): UseWeb3Return => {
  const [account, setAccount] = useState<string | null>(null);
  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
  const [signer, setSigner] = useState<ethers.JsonRpcSigner | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isManuallyDisconnectedRef = useRef(false);

  useEffect(() => {
    // Only check connection on mount if not manually disconnected
    if (!isManuallyDisconnectedRef.current) {
      checkConnection();
    }
    
    // Listen for account changes
    if (typeof window !== 'undefined' && window.ethereum) {
      const handleAccountsChanged = (accounts: string[]) => {
        // CRITICAL: Ignore ALL account changes if manually disconnected
        if (isManuallyDisconnectedRef.current) {
          console.log('Ignoring accountsChanged event - wallet was manually disconnected');
          return;
        }
        
        if (accounts.length === 0) {
          setAccount(null);
          setProvider(null);
          setSigner(null);
        } else {
          // Only reconnect if not manually disconnected
          if (!isManuallyDisconnectedRef.current) {
            checkConnection();
          }
        }
      };

      const handleChainChanged = () => {
        // Reload page on chain change (but respect manual disconnect)
        if (!isManuallyDisconnectedRef.current) {
          window.location.reload();
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      
      return () => {
        window.ethereum?.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum?.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, []); // Empty dependency array - only run on mount

  const checkConnection = async () => {
    // CRITICAL: Don't check if manually disconnected - this prevents auto-reconnection
    if (isManuallyDisconnectedRef.current) {
      console.log('Skipping checkConnection - wallet was manually disconnected');
      return;
    }
    
    try {
      const result = await getCurrentAccount();
      if (result) {
        // Double-check flag before setting account (race condition protection)
        if (!isManuallyDisconnectedRef.current) {
          setAccount(result.address);
          setProvider(result.provider);
          setSigner(result.signer);
        }
      } else {
        // No account found, clear state (but only if not manually disconnected)
        if (!isManuallyDisconnectedRef.current) {
          setAccount(null);
          setProvider(null);
          setSigner(null);
        }
      }
    } catch (err) {
      console.error('Error checking connection:', err);
      // Only clear state if not manually disconnected
      if (!isManuallyDisconnectedRef.current) {
        setAccount(null);
        setProvider(null);
        setSigner(null);
      }
    }
  };

  const connect = async () => {
    setLoading(true);
    setError(null);
    isManuallyDisconnectedRef.current = false;

    try {
      // Ensure we're on Polygon Amoy
      await switchToPolygonAmoy();
      
      const result = await connectWallet();
      setAccount(result.address);
      setProvider(result.provider);
      setSigner(result.signer);
      isManuallyDisconnectedRef.current = false;
    } catch (err: any) {
      setError(err.message || 'Failed to connect wallet');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const disconnect = async () => {
    console.log('Disconnecting wallet...');
    
    // CRITICAL: Set flag FIRST before any other operations
    // This prevents any automatic reconnection attempts
    isManuallyDisconnectedRef.current = true;
    
    // Clear state immediately
    setAccount(null);
    setProvider(null);
    setSigner(null);
    
    // Try to disconnect from MetaMask programmatically
    // Note: MetaMask doesn't have a direct disconnect API, but we can try to revoke permissions
    if (typeof window !== 'undefined' && window.ethereum) {
      try {
        // Try to revoke permissions (this may not work in all MetaMask versions)
        await window.ethereum.request({
          method: 'wallet_revokePermissions',
          params: [{ eth_accounts: {} }],
        });
        console.log('Permissions revoked successfully');
      } catch (err: any) {
        // Ignore errors - MetaMask may not support this or permissions may not be revocable
        // This is normal behavior - the flag will prevent reconnection anyway
        console.log('Could not revoke permissions (this is normal):', err?.message || err);
      }
    }
    
    // Force a delay to ensure state is cleared and flag is set
    await new Promise(resolve => setTimeout(resolve, 200));
    
    console.log('Wallet disconnected. Flag set to prevent auto-reconnection.');
  };

  return {
    account,
    provider,
    signer,
    connect,
    disconnect,
    loading,
    error,
  };
};

